import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, ShieldCheck, Truck, Gift, MessageSquareHeart, BadgeCheck, RefreshCcw, Headset, Sparkles } from "lucide-react";
import { api, inr } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { Reveal, MaskedLines, SectionHead, Marquee, EASE } from "../components/Motion";
import ProductCard, { ComingSoonBadge } from "../components/ProductCard";
import GiftFinder from "../components/GiftFinder";
import NotifyMe from "../components/NotifyMe";
import ContestSection from "../components/ContestSection";
import { FESTIVALS, OCCASIONS, RECIPIENTS, label } from "../data/taxonomy";

const WHY_ICONS = [Gift, ShieldCheck, Sparkles, MessageSquareHeart, Truck, Headset, RefreshCcw, BadgeCheck];

function Hero({ sonata }) {
  const reduced = useReducedMotion();
  const { addToCart, settings } = useStore();
  const heroLines = settings?.hero_title ? settings.hero_title.split("\n").filter(Boolean) : null;
  const heroSub = settings?.hero_subtitle || "Make every occasion special with unique, high-quality gifts for your loved ones — perfect for birthdays, anniversaries, festivals and all life's moments.";
  const heroImg = settings?.hero_image || "/assets/sonata-2.png";
  const ctaPrimary = settings?.hero_cta_primary || "Explore Gifts";
  const ctaSecondary = settings?.hero_cta_secondary || "Shop Sonata";
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const watchX = useTransform(sx, (v) => v * 14);
  const watchY = useTransform(sy, (v) => v * 10);
  const glowX = useTransform(sx, (v) => v * 5);
  const glowY = useTransform(sy, (v) => v * 4);
  const decoX = useTransform(sx, (v) => v * -8);
  const decoY = useTransform(sy, (v) => v * -6);
  const ref = useRef(null);
  const navigate = useNavigate();

  const onMove = (e) => {
    if (reduced || e.pointerType === "touch" || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 2);
    my.set(((e.clientY - r.top) / r.height - 0.5) * 2);
  };

  return (
    <section ref={ref} onPointerMove={onMove} className="relative lg:min-h-[100svh] flex items-center overflow-hidden" data-testid="hero-section"
      style={{ background: "radial-gradient(ellipse at 50% 30%, rgba(126,30,63,0.10) 0%, rgba(251,247,241,0.94) 62%, #FFFFFF 100%)" }}>
      <motion.div style={reduced ? {} : { x: decoX, y: decoY }} className="absolute top-16 right-4 w-24 h-24 rounded-full border border-[#7E1E3F]/15 pointer-events-none lg:top-24 lg:left-[8%] lg:right-auto lg:w-40 lg:h-40" />
      <motion.div style={reduced ? {} : { x: glowX, y: glowY }} className="absolute bottom-6 right-[8%] w-36 h-36 rounded-full border border-[#7E1E3F]/10 pointer-events-none lg:bottom-16 lg:right-[6%] lg:w-56 lg:h-56" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-24 pb-10 grid lg:grid-cols-2 gap-10 lg:gap-12 lg:pt-28 lg:pb-16 items-center w-full">
        <div>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.2 }}
            className="font-mono-lux text-[11px] uppercase tracking-[0.35em] text-[#7E1E3F] mb-6">
            The Art of Thoughtful Gifting
          </motion.p>
          <h1 className="font-serif-lux text-4xl sm:text-5xl lg:text-[64px] leading-[1.05] tracking-tight text-[#2E1A22] text-balance" data-testid="hero-title">
            <MaskedLines delay={0.35} lines={heroLines || ["Gifts That Make", <span key="g" className="gold-text italic">Moments</span>, "Memorable"]} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.95, ease: EASE }}
            className="mt-6 text-base sm:text-lg text-[#857078] max-w-md leading-relaxed">
            {heroSub}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.1, ease: EASE }}
            className="mt-8 lg:mt-9 flex flex-wrap gap-4">
            <Link to="/gifts" data-testid="hero-cta-explore"
              className="group inline-flex items-center gap-2 bg-[#7E1E3F] text-[#FFFFFF] font-semibold text-sm tracking-wider uppercase px-8 py-4 rounded-full hover:bg-[#5C1230] transition-colors active:scale-[0.98]">
              {ctaPrimary} <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.35 }}
            className="mt-8 lg:mt-12 flex flex-wrap gap-x-8 gap-y-3 text-[11px] font-mono-lux tracking-[0.2em] uppercase text-[#857078]">
            <span className="flex items-center gap-2"><Gift size={13} className="text-[#7E1E3F]" /> Premium Packaging</span>
            <span className="flex items-center gap-2"><Truck size={13} className="text-[#7E1E3F]" /> 2–4 Day Est. Delivery</span>
            <span className="flex items-center gap-2"><ShieldCheck size={13} className="text-[#7E1E3F]" /> Secure Prepaid Checkout</span>
          </motion.div>
        </div>

        <div className="relative flex justify-center" data-testid="hero-sonata-watch-container">
          <motion.div style={reduced ? {} : { x: glowX, y: glowY }}
            className="absolute w-[70%] aspect-square rounded-full bg-[#7E1E3F]/15 blur-3xl" />
          <motion.div
            initial={reduced ? false : { scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.1, delay: 0.7, ease: EASE }}
            style={reduced ? {} : { x: watchX, y: watchY }}
            className="relative">
            <div className={reduced ? "" : "animate-floaty"}>
              <img
                src={heroImg}
                alt="Sonata SF Ocean Series Watch"
                className="relative z-10 w-[240px] sm:w-[300px] lg:w-[380px] aspect-square object-contain bg-white rounded-[28px] border border-[#7E1E3F]/35 shadow-[0_40px_90px_rgba(52,58,70,0.28)] p-4"
              />
              <div className="absolute inset-0 z-20 rounded-[28px] bg-gradient-to-tr from-transparent via-white/5 to-white/15 pointer-events-none" />
            </div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.4 }}
              className={`mx-auto mt-6 h-5 w-[65%] rounded-[100%] bg-black/70 blur-md ${reduced ? "" : "animate-floaty-shadow"}`} />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function SonataShowcase({ sonata }) {
  const { addToCart } = useStore();
  const navigate = useNavigate();
  if (!sonata) return null;
  const off = Math.round(((sonata.mrp - sonata.price) / sonata.mrp) * 100);
  return (
    <section className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal className="relative">
          <div className="absolute -inset-6 bg-[#7E1E3F]/8 blur-3xl rounded-full pointer-events-none" />
          <div className="relative card-surface rounded-3xl p-4 overflow-hidden">
            <img src={sonata.images[0]} alt={sonata.name} loading="lazy"
              className={`w-full aspect-[4/5] rounded-2xl ${sonata.images[0].startsWith("/assets/") || sonata.images[0].startsWith("/api/") ? "object-contain bg-white p-6" : "object-cover"}`} data-testid="featured-sonata-image" />
            <span className="absolute top-8 left-8 bg-[#7E1E3F] text-[#FFFFFF] text-[10px] font-bold tracking-widest uppercase px-3 py-1.5 rounded-full">Available Now</span>
          </div>
        </Reveal>
        <div data-testid="featured-sonata-card">
          <SectionHead overline="The Crown Jewel" title="Make Every Moment Count"
            sub="Our launch piece — the Sonata SF Ocean Series. One watch, a thousand occasions." />
          <Reveal delay={0.1}>
            <h3 className="font-serif-lux text-2xl text-[#2E1A22]">{sonata.name}</h3>
            <div className="mt-3 flex items-baseline gap-3" data-testid="sonata-price-display">
              <span className="text-3xl text-[#7E1E3F] font-semibold">{inr(sonata.price)}</span>
              <span className="text-lg text-[#857078] line-through">{inr(sonata.mrp)}</span>
              <span className="text-xs bg-[#2A9D8F]/20 text-[#2A9D8F] px-2.5 py-1 rounded-full font-medium">{off}% OFF</span>
            </div>
            <p className="mt-3 text-sm text-[#5C1230] font-mono-lux tracking-wide">{sonata.bundle?.label}</p>
          </Reveal>
          <Reveal delay={0.2} className="mt-8 flex flex-wrap gap-4">
            <button onClick={() => addToCart(sonata)} data-testid="sonata-add-to-cart-button"
              className="border border-[#7E1E3F]/50 text-[#5C1230] text-sm tracking-wider uppercase font-medium px-8 py-4 rounded-full hover:bg-[#7E1E3F]/10 transition-colors">
              Add to Cart
            </button>
            <button onClick={() => { addToCart(sonata); navigate("/checkout"); }} data-testid="sonata-buy-now-button"
              className="group bg-[#7E1E3F] text-[#FFFFFF] text-sm tracking-wider uppercase font-semibold px-8 py-4 rounded-full hover:bg-[#5C1230] transition-colors inline-flex items-center gap-2 active:scale-[0.98]">
              Buy Now <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ImageCard({ img, name, to, sub, testid, tall = false }) {
  return (
    <Link to={to} data-testid={testid}
      className={`group relative overflow-hidden rounded-2xl card-surface gold-ring-hover hover:-translate-y-1.5 block ${tall ? "aspect-[3/4]" : "aspect-[4/5]"}`}>
      <img src={img} alt={name} loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-[transform,filter] duration-700 group-hover:scale-[1.06] group-hover:brightness-110" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#FFFFFF]/95 via-[#FFFFFF]/20 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-5">
        <h3 className="font-serif-lux text-xl text-[#2E1A22] transition-transform duration-500 group-hover:-translate-y-1">{name}</h3>
        {sub && <p className="text-xs text-[#857078] mt-1 opacity-80">{sub}</p>}
        <p className="text-[11px] font-mono-lux tracking-[0.2em] uppercase text-[#7E1E3F] mt-2 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-500">
          Explore Gifts →
        </p>
      </div>
    </Link>
  );
}

export default function Home() {
  const [sonata, setSonata] = useState(null);
  const [sections, setSections] = useState({ dry: [], hampers: [], personalized: [] });
  const [faqs, setFaqs] = useState([]);
  const [notifyProduct, setNotifyProduct] = useState(null);
  const { settings } = useStore();
  const c = (k, d) => settings?.content?.[k] || d;
  const lines = (k, d) => (settings?.content?.[k] ? settings.content[k].split("\n").filter(Boolean) : d);
  const occImgs = settings?.occasion_images || {};
  const festImgs = settings?.festival_images || {};

  useEffect(() => {
    if (settings?.site_title) document.title = settings.site_title;
    const meta = document.querySelector('meta[name="description"]');
    if (meta && settings?.site_description) meta.setAttribute("content", settings.site_description);
  }, [settings]);

  useEffect(() => {
    api.get("/products/sonata-sf-ocean-series-np7989pp01").then((r) => setSonata(r.data)).catch(() => {});
    Promise.all([
      api.get("/products?category=dry-fruits"),
      api.get("/products?category=gift-hampers"),
      api.get("/products?category=personalized"),
    ]).then(([d, h, p]) => setSections({
      dry: d.data.products.slice(0, 4), hampers: h.data.products.slice(0, 4), personalized: p.data.products.slice(0, 4),
    })).catch(() => {});
    api.get("/faqs").then((r) => setFaqs(r.data.faqs.slice(0, 5))).catch(() => {});
  }, []);

  const dryHero = sections.dry[0];
  const dryHighlights = lines("dry_highlights", ["Premium handpicked dry fruits", "Elegant luxury wooden box", "Gift message option", "Secure delivery packaging"]);
  const whyItems = lines("why_items", ["Premium Gifting", "Secure Online Payments", "Thoughtful Presentation", "Gift Messages Available", "2–4 Business Day Est. Delivery", "Customer Support", "Easy Exchange on Eligible Products", "Genuine Branded Products"]);
  const chapters = lines("story_chapters", [
    "The Obsession with Detailing | Every Savyron gift is inspected, wrapped and finished by hand. If a ribbon sits a millimetre wrong, we tie it again.",
    "The Heritage of Indian Hospitality | In India, a gift is never just an object — it is a blessing, a memory, a relationship. We build every box with that weight in mind.",
    "The Unboxing Theater | From the weight of the box to the fall of the ribbon, we design the moment of opening to feel like a small ceremony.",
  ]).map((l) => { const i = l.indexOf("|"); return i > 0 ? [l.slice(0, i).trim(), l.slice(i + 1).trim()] : [l, ""]; });

  const gridSections = [
    { title: c("hampers_title", "Gift Hampers"), sub: c("hampers_sub", "Curated boxes for birthdays, anniversaries and every celebration in between."), items: sections.hampers, link: "/gifts/gift-hampers", tid: "gift-hampers-section" },
    { title: c("pers_title", "Personalized Gifts"), sub: c("pers_sub", "Their name, your words — gifts made one of one."), items: sections.personalized, link: "/gifts/personalized", tid: "personalized-section" },
  ];

  return (
    <div>
      <Hero sonata={sonata} />
      <SonataShowcase sonata={sonata} />
      <Marquee items={lines("marquee", null)} />

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHead overline={c("occ_overline", "Shop by Occasion")} title={c("occ_title", "Every Occasion, Beautifully Boxed")} testid="occasion-section-head" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5" data-testid="occasion-grid">
            {OCCASIONS.slice(0, 8).map((o, i) => (
              <Reveal key={o.slug} delay={i * 0.06}>
                <ImageCard img={occImgs[o.slug] || o.img} name={o.name} to={`/occasion/${o.slug}`} testid={`occasion-card-${o.slug}`} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#FBF7F1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHead overline={c("fest_overline", "Shop by Festival")} title={c("fest_title", "Festivals Deserve Gold")}
            sub={c("fest_sub", "From Diwali diyas to New Year sparkle — gifting for every celebration on the Indian calendar.")} testid="festival-section" />
          <div className="flex gap-5 overflow-x-auto pb-4 snap-x" style={{ scrollbarWidth: "thin" }}>
            {FESTIVALS.map((f, i) => (
              <motion.div key={f.slug} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.6, delay: (i % 5) * 0.07, ease: EASE }}
                className="snap-start shrink-0 w-[240px] sm:w-[270px]">
                <ImageCard img={festImgs[f.slug] || f.img} name={f.name} sub={f.desc} to={`/festival/${f.slug}`} testid={`festival-card-${f.slug}`} tall />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHead overline={c("rec_overline", "Shop by Recipient")} title={c("rec_title", "For Everyone You Love")} />
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            {RECIPIENTS.slice(0, 14).map((r, i) => (
              <Reveal key={r.slug} delay={(i % 7) * 0.05}>
                <Link to={`/recipient/${r.slug}`} data-testid={`recipient-card-${r.slug}`}
                  className="block border border-[#7E1E3F]/20 rounded-xl px-3 py-4 text-center text-sm text-[#45303A] hover:border-[#7E1E3F] hover:bg-[#7E1E3F]/8 hover:-translate-y-1 transition-[border-color,background-color,transform] duration-300">
                  {r.name}
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <GiftFinder onNotify={setNotifyProduct} title={c("finder_title", "")} />
        </div>
      </section>

      <section className="py-24 sm:py-32 relative overflow-hidden" data-testid="dry-fruits-section">
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 30% 50%, rgba(200,164,93,0.07) 0%, transparent 60%)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-14 items-center relative">
          <div>
            <SectionHead overline={c("dry_overline", "Coming Soon · Signature")} title={c("dry_title", "Premium Dry Fruit Hampers")}
              sub={c("dry_sub", "A Premium Gift of Taste, Health & Elegance — almonds, cashews, pistachios, raisins and walnuts in a luxury wooden box.")} />
            <div className="space-y-3">
              {dryHighlights.map((h, i) => (
                <Reveal key={h} delay={0.15 + i * 0.1}>
                  <p className="flex items-center gap-3 text-sm text-[#45303A]">
                    <span className="w-6 h-6 rounded-full border border-[#7E1E3F]/40 flex items-center justify-center text-[#7E1E3F] text-[10px] font-mono-lux">{i + 1}</span>
                    {h}
                  </p>
                </Reveal>
              ))}
            </div>
            {dryHero && (
              <Reveal delay={0.5} className="mt-8">
                <button onClick={() => setNotifyProduct(dryHero)} data-testid="dry-fruits-notify-button"
                  className="border border-[#7E1E3F]/50 text-[#5C1230] text-sm tracking-wider uppercase font-medium px-8 py-4 rounded-full hover:bg-[#7E1E3F]/10 transition-colors">
                  {c("dry_cta", "Notify Me at Launch")}
                </button>
              </Reveal>
            )}
          </div>
          <Reveal className="relative">
            <motion.div initial={{ scale: 0.92, opacity: 0 }} whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }} transition={{ duration: 1, ease: EASE }} className="relative">
              <div className="card-surface rounded-3xl p-4 relative">
                <img src={c("dry_image", "") || dryHero?.images[0]} alt="Premium Dry Fruit Hamper in Luxury Wooden Box" loading="lazy"
                  className="w-full aspect-[4/3] object-cover rounded-2xl" />
                <div className="absolute top-8 left-8"><ComingSoonBadge /></div>
              </div>
              <div className="mx-auto mt-5 h-4 w-[60%] rounded-[100%] bg-black/60 blur-md" />
            </motion.div>
          </Reveal>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {sections.dry.map((p, i) => <ProductCard key={p.slug} product={p} index={i} onNotify={setNotifyProduct} />)}
          </div>
        </div>
      </section>

      {gridSections.map(({ title, sub, items, link, tid }) => (
        <section key={tid} className="py-20" data-testid={tid}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
              <SectionHead overline="Coming Soon Collection" title={title} sub={sub} />
              <Link to={link} className="text-xs font-mono-lux tracking-[0.2em] uppercase text-[#7E1E3F] hover:text-[#5C1230] transition-colors mb-1" data-testid={`${tid}-view-all`}>
                View All →
              </Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {items.map((p, i) => <ProductCard key={p.slug} product={p} index={i} onNotify={setNotifyProduct} />)}
            </div>
          </div>
        </section>
      ))}

      <section className="py-24 bg-[#FBF7F1]" data-testid="why-savyron">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHead overline={c("why_overline", "Why Savyron")} title={c("why_title", "Why Shop With Savyron?")} center />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {whyItems.map((t, i) => {
              const Icon = WHY_ICONS[i % WHY_ICONS.length];
              return (
                <Reveal key={t} delay={(i % 4) * 0.06}>
                  <div className="card-surface rounded-2xl p-5 text-center h-full">
                    <Icon className="mx-auto text-[#7E1E3F]" size={22} />
                    <p className="mt-3 text-xs sm:text-sm text-[#45303A] leading-snug">{t}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32" id="story" data-testid="savyron-manifesto-story">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <SectionHead overline={c("story_overline", "The Savyron Story")} title={c("story_title", "A Gift They'll Remember")} center />
          <div className="space-y-14 mt-6">
            {chapters.map(([title, text], i) => (
              <Reveal key={title} delay={i * 0.08}>
                <div className="flex gap-6 sm:gap-10 items-start border-t border-[#7E1E3F]/15 pt-10">
                  <span className="font-serif-lux text-5xl sm:text-6xl gold-text leading-none">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h3 className="font-serif-lux text-2xl sm:text-3xl text-[#2E1A22]">{title}</h3>
                    <p className="mt-3 text-sm sm:text-base text-[#857078] leading-relaxed max-w-xl">{text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#FBF7F1]" data-testid="reviews-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <SectionHead overline={c("reviews_overline", "Reviews")} title={c("reviews_title", "Words From Our Gifters")} center />
          <Reveal>
            <div className="card-surface rounded-2xl p-10">
              <p className="font-serif-lux text-xl text-[#857078] italic">"{c("reviews_empty", "Be the first to review this product.")}"</p>
              <p className="text-sm text-[#857078] mt-3">{c("reviews_empty_sub", "We only publish reviews from verified purchases — never invented, never bought.")}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <ContestSection />

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
          <SectionHead overline={c("corp_overline", "Corporate Gifting")} title={c("corp_title", "Thoughtful gifting for clients, teams and special business occasions.")}
            sub={c("corp_sub", "Bulk dry fruit boxes, branded hampers and concierge curation — tell us the occasion and budget, we handle the rest.")} />
          <Reveal delay={0.15} className="lg:text-right">
            <Link to="/corporate" data-testid="corporate-cta"
              className="inline-flex items-center gap-2 bg-[#7E1E3F] text-[#FFFFFF] font-semibold text-sm tracking-wider uppercase px-8 py-4 rounded-full hover:bg-[#5C1230] transition-colors">
              {c("corp_cta", "Request a Quote")} <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {faqs.length > 0 && (
        <section className="py-24 bg-[#FBF7F1]" data-testid="faq-section">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <SectionHead overline={c("faq_overline", "Questions")} title={c("faq_title", "Before You Ask")} center />
            <div className="space-y-3">
              {faqs.map((f) => (
                <Reveal key={f.id}>
                  <details className="card-surface rounded-xl px-5 py-4 group">
                    <summary className="cursor-pointer list-none flex justify-between items-center text-[#45303A] text-sm sm:text-base font-medium">
                      {f.question}
                      <span className="text-[#7E1E3F] ml-4 transition-transform duration-300 group-open:rotate-45">+</span>
                    </summary>
                    <p className="mt-3 text-sm text-[#857078] leading-relaxed">{f.answer}</p>
                  </details>
                </Reveal>
              ))}
            </div>
            <Reveal className="text-center mt-10">
              <Link to="/faq" className="text-xs font-mono-lux tracking-[0.2em] uppercase text-[#7E1E3F] hover:text-[#5C1230]" data-testid="faq-view-all">View All FAQs →</Link>
            </Reveal>
          </div>
        </section>
      )}

      <NotifyMe product={notifyProduct} onClose={() => setNotifyProduct(null)} />
    </div>
  );
}
